import { createRouter, createWebHistory } from "vue-router";
import store from "../store";
import Login from "@/views/auth/login";
import Register from "@/views/auth/register";
import ResetPassword from "@/views/auth/reset-password";
import CometDataExplorer from "@/views/comet/CometDataExplorer";
import LeagueData from "@/views/leagueData";
import PayFees from "@/views/payFees";
import TrialsRegister from "@/views/trialsRegister";
import MyPayments from "@/views/myPayments";
import TrainingPlans from "@/views/training-plans";
import ExerciseLibrary from "@/views/exerciseLibrary";
import PhysioCalendar from "@/views/physioCalendar";
import BookPhysio from "@/views/bookPhysio";
import Teams from "@/views/teams";
import Coaches from "@/views/coaches";
import ReturnToPlay from "@/views/returnToPlay";
import Scouts from "@/views/scouts";
import SeasonRollover from "@/views/seasonRollover";
import Chats from "@/views/chat";
import injuries from "@/views/injuries";
import reports from "@/views/reports";
import matchReports from "@/views/matchReports";
import gpsReports from "@/views/allGPSreports";
import Booking from "@/views/booking";
import Documents from "@/views/documents";
import Help from "@/views/help";
import Settings from "@/views/settings";
import Trialists from "@/views/trialist";
import InvitationSignUp from "@/views/trialist/invitation-sign-up";
import PasswordRecovery from "@/views/passwordRecovery";
import Home from "@/views/home";
import Dashboard from "@/views/dashboard";
import Profile from "@/views/profile";
import PtUserHome from "@/views/home";
import RegistrationPaymentComplete from "@/views/auth/registration-payment-complete";
import SupportCampaign from "@/views/support";
import PrivacyPolicy from "@/views/legal/PrivacyPolicy.vue";
import DeleteAccount from "@/views/legal/DeleteAccount.vue";
import TermsAndConditions from "@/views/legal/TermsAndConditions.vue";
import Nutrition from "@/views/nutrition";
import Testing from "@/views/testing";
import { Users } from "@/services/user-service.js";
import { USER_POPULATE } from "@/store/actions/user.js";
import { AUTH_SUCCESS, AUTH_REQUEST } from "@/store/actions/auth";
import { isFeatureEnabled } from "@/config/tenant";

// Router is now imported directly from vue-router

// Restricted roles: the ONLY routes these user types may open. Any other
// userType (Admin, Coach, User…) is unrestricted here — pages still apply
// their own gates. Until this existed a "restricted" role was only hidden
// tiles on the dashboard; typing /profile worked.
const ROLE_ROUTES = {
  // Route NAMES ("home" is /dashboard).
  Scout: ["home", "trialists", "scouts", "booking", "documents", "help", "nutrition"],
  // Volunteer sports scientists: physical-testing entry + their club chat
  // groups. No profile page (that's where the welfare data lives).
  SportsScience: ["home", "testing", "chats", "help"],
};
const ALWAYS_ALLOWED = ["privacyPolicy", "termsAndConditions", "deleteAccount", "login"];

function roleAllowsRoute(userType, routeName) {
  const allowed = ROLE_ROUTES[userType];
  if (!allowed) return true;
  return allowed.includes(routeName) || ALWAYS_ALLOWED.includes(routeName);
}

async function guardMyroute(to, from, next) {
  await checkIfUserHasToken();
  var isAuthenticated = false;
  if (await store.getters.isAuthenticated) {
    isAuthenticated = true;
  } else isAuthenticated = false;

  if (isAuthenticated) {
    // Tier gating: routes tagged with meta.feature are only reachable when
    // the tenant's tier/config enables that feature (see config/tiers.json)
    if (to.meta?.feature && !isFeatureEnabled(to.meta.feature)) {
      next("/dashboard");
      return;
    }
    const userType = store.getters.getProfile?.userType;
    if (!roleAllowsRoute(userType, to.name)) {
      next("/dashboard");
      return;
    }
    next(); // allow to enter route
  } else {
    next("/login"); // go to '/login';
  }
}

function getLastTokenSegment() {
  try {
    const userToken = localStorage.getItem("user-token");

    if (!userToken) {
      console.warn("No user token found in localStorage");
      return null;
    }

    const tokenSegments = userToken.split("/");

    if (tokenSegments.length === 0) {
      console.warn("Token has no segments after splitting");
      return null;
    }

    return tokenSegments[tokenSegments.length - 1];
  } catch (error) {
    console.error("Error processing user token:", error);
    return null;
  }
}

async function getUserById(clientId) {
  const userData = {
    clientId: clientId,
    action: "getUserByID",
  };
  const responce = await Users.find(userData).catch((error) => {
    console.log(error);
  });
  if (responce.data.length > 0) {
    return responce.data;
  }
}

async function checkIfUserHasToken() {
  if (localStorage.getItem("user-token") !== null) {
    store.commit(AUTH_REQUEST);
    let userId = getLastTokenSegment();

    const userData = await getUserById(userId);
    console.log(userData);
    localStorage.setItem("the_w_selectedClientId", userData[0].id);
    store.dispatch(USER_POPULATE, { userData: userData[0] });
    store.commit(AUTH_SUCCESS, userData[0]);
  }
}

/*function logout() {
  store.dispatch("AUTH_LOGOUT");
  router.push({ name: "login" });
  location.reload();
}

const ifAuthenticated = (to, from, next) => {
  if (store.getters.isAuthenticated) {
    next();
    return;
  }
  next("/login");
};*/

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  base: process.env.BASE_URL,
  routes: [
    // The site root had no route and there is no catch-all, so
    // https://<tenant>/ rendered a blank white page. The staff invite email
    // says "Sign in at https://<tenant>/", so every invited coach/admin
    // landed on nothing — the PWA start_url is /login, which is why regular
    // users never hit it. Send the root (and any unknown path) somewhere real:
    // the dashboard when a session token is present, otherwise the login page.
    {
      path: "/",
      redirect: () =>
        localStorage.getItem("user-token") ? "/dashboard" : "/login",
    },
    {
      path: "/dashboard",
      name: "home",
      component: Dashboard,
      beforeEnter: guardMyroute,
    },
    {
      path: "/pt-dashboard",
      name: "ptHome",
      component: Home,
      beforeEnter: guardMyroute,
      meta: { feature: "trainingPlans" },
    },
    {
      path: "/pt-user-dashboard",
      name: "ptUserHome",
      component: PtUserHome,
      beforeEnter: guardMyroute,
      meta: { feature: "trainingPlans" },
    },
    {
      path: "/profile",
      name: "profile",
      component: Profile,
      beforeEnter: guardMyroute,
    },
    {
      // Physical-testing entry for any squad (staff + SportsScience role).
      path: "/testing",
      name: "testing",
      component: Testing,
      beforeEnter: guardMyroute,
      meta: { feature: "gpsReports" },
    },
    {
      path: "/settings",
      name: "Settings",
      component: Settings,
      beforeEnter: guardMyroute,
    },
    {
      path: "/nutrition",
      name: "nutrition",
      component: Nutrition,
      beforeEnter: guardMyroute,
      meta: { feature: "nutrition" },
    },
    {
      // Player/parent-facing: pay fees, season installments, vouchers, history
      path: "/my-payments",
      name: "myPayments",
      component: MyPayments,
      beforeEnter: guardMyroute,
      meta: { feature: "paymentProcessing" },
    },
    {
      path: "/teams",
      name: "teams",
      component: Teams,
      beforeEnter: guardMyroute,
    },
    {
      path: "/trialists",
      name: "trialists",
      component: Trialists,
      beforeEnter: guardMyroute,
      meta: { feature: "trialists" },
    },
    {
      path: "/trialists/invite/:token",
      name: "invitationSignUpToken",
      component: InvitationSignUp,
    },
    {
      path: "/trialists/invitation-sign-up/:clubId/:trialistId",
      name: "invitationSignUp",
      component: InvitationSignUp,
    },
    {
      path: "/coaches",
      name: "coaches",
      component: Coaches,
      beforeEnter: guardMyroute,
    },
    {
      path: "/scouts",
      name: "scouts",
      component: Scouts,
      beforeEnter: guardMyroute,
      meta: { feature: "scouts" },
    },
    {
      path: "/season-rollover",
      name: "seasonRollover",
      component: SeasonRollover,
      beforeEnter: guardMyroute,
      meta: { feature: "seasonRollover" },
    },
    {
      path: "/booking",
      name: "booking",
      component: Booking,
      beforeEnter: guardMyroute,
      meta: { feature: "booking" },
    },
    {
      path: "/injuries",
      name: "injuries",
      component: injuries,
      beforeEnter: guardMyroute,
      meta: { feature: "injuryManagement" },
    },
    {
      path: "/reports",
      name: "reports",
      component: reports,
      beforeEnter: guardMyroute,
      meta: { feature: "appActivity" },
    },
    {
      path: "/training-plans/:trainingPlanUserId",
      name: "trainingPlans",
      component: TrainingPlans,
      props: true,
      beforeEnter: guardMyroute,
      meta: { feature: "trainingPlans" },
    },
    {
      path: "/exercise-library",
      name: "exerciseLibrary",
      component: ExerciseLibrary,
      beforeEnter: guardMyroute,
      meta: { feature: "exerciseLibrary" },
    },
    {
      path: "/documents/",
      name: "documents",
      component: Documents,
      beforeEnter: guardMyroute,
      meta: { feature: "documents" },
    },
    {
      path: "/help/",
      name: "help",
      component: Help,
      beforeEnter: guardMyroute,
    },
    {
      path: "/login",
      name: "login",
      component: Login,
      // beforeEnter: guardMyroute
    },
    {
      path: "/reset-password",
      name: "resetPassword",
      component: ResetPassword,
    },
    {
      path: "/register/:clubId",
      name: "register",
      component: Register,
      // beforeEnter: guardMyroute
    },
    {
      path: "/forgot-password/:key",
      name: "passwordRecovery",
      component: PasswordRecovery,
      // beforeEnter: guardMyroute
    },
    {
      // Public: Stripe Checkout redirects back here after a registration
      // fee payment (sessionId is "cancelled" for the cancel URL)
      path: "/registration/payment-complete/:sessionId",
      name: "registrationPaymentComplete",
      component: RegistrationPaymentComplete,
    },
    {
      // Public: fundraising campaign donation page (shared by link/QR)
      path: "/support/:clubId/:campaignId",
      name: "supportCampaign",
      component: SupportCampaign,
    },
    {
      // Public: permanent season-fees pay page (printable in newsletters —
      // checkout sessions are minted on click, so the URL never expires)
      path: "/pay-fees",
      name: "payFees",
      component: PayFees,
    },
    {
      // Public: grassroots trials Expression of Interest form
      path: "/trials/:clubId",
      name: "trialsRegister",
      component: TrialsRegister,
    },
    {
      path: "/chats",
      name: "chats",
      component: Chats,
      beforeEnter: guardMyroute,
      meta: { feature: "realTimeChat" },
    },
    {
      path: "/match-reports",
      name: "matchReports",
      component: matchReports,
      beforeEnter: guardMyroute,
      meta: { feature: "matchReports" },
    },

    {
      path: "/gps-reports",
      name: "gpsReports",
      component: gpsReports,
      beforeEnter: guardMyroute,
      meta: { feature: "gpsReports" },
    },
    {
      path: "/comet-explorer",
      name: "cometExplorer",
      component: CometDataExplorer,
      beforeEnter: guardMyroute,
      meta: { feature: "cometExplorer" },
    },
    {
      path: "/league-data",
      name: "leagueData",
      component: LeagueData,
      beforeEnter: guardMyroute,
    },
    {
      path: "/return-to-play",
      name: "returnToPlay",
      component: ReturnToPlay,
      beforeEnter: guardMyroute,
      meta: { feature: "returnToPlay" },
    },
    {
      path: "/privacy-policy",
      name: "privacyPolicy",
      component: PrivacyPolicy,
    },
    {
      path: "/delete-account",
      name: "deleteAccount",
      component: DeleteAccount,
    },
    {
      path: "/terms",
      name: "termsAndConditions",
      component: TermsAndConditions,
    },
    {
      path: "/physio-calendar",
      name: "physioCalendar",
      component: PhysioCalendar,
      beforeEnter: guardMyroute,
      meta: { feature: "physioAppointments" },
    },
    {
      path: "/book-physio",
      name: "bookPhysio",
      component: BookPhysio,
      beforeEnter: guardMyroute,
      meta: { feature: "physioAppointments" },
    },
    // Unknown paths (mistyped links, stale bookmarks) previously rendered a
    // blank page too. Keep this LAST so it only catches what nothing else did.
    {
      path: "/:pathMatch(.*)*",
      redirect: () =>
        localStorage.getItem("user-token") ? "/dashboard" : "/login",
    },
  ],
});
export default router;
